import produce from 'immer';
import isEqual from 'react-fast-compare';

import {UnknownStructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import {Cell} from 'notebook/tables/constants/CellConstants';

/**
 * Why a draft edit can't be applied to the live table as is:
 *  - `deleted`: the edited row or column is no longer in the table.
 *  - `finalized`: the edited row was finalized and the cell is now read-only.
 *  - `read-only`: the cell is read-only for another reason, such as its row being tombstoned.
 *  - `changed`: someone else changed the cell to a different value.
 */
export type TableConflictReason = 'deleted' | 'finalized' | 'read-only' | 'changed';

/** `theirs` keeps the live cell and drops the edit; `overwrite` applies the edit over it. */
export type TableConflictResolution = 'theirs' | 'overwrite';

export interface TableConflict {
  /** Identifies the edited cell across tables; see `tableCellKey`. */
  key: string;
  rowId: string;
  columnUuid: string;
  reason: TableConflictReason;
  /** The row's index in `base`, for labeling the conflict. */
  baseRowIndex: number;
  draftCell: Cell;
  liveCell: Cell | null;
}

/** A resolution, with the conflict as the user saw it when choosing. */
export interface TableConflictDecision {
  resolution: TableConflictResolution;
  conflict: TableConflict;
}

/** Decisions by conflict key. */
export type TableConflictDecisions = ReadonlyMap<string, TableConflictDecision>;

export interface MergeResult {
  /** `live` with the applicable edits. The same object as `live` when no edit applies. */
  next: UnknownStructuredTable;
  /** Edits that can't be applied as is and have no resolution that still applies. */
  conflicts: Array<TableConflict>;
}

/** Whether a cell in `table` is read-only, as the table's editor would show it. */
export type IsCellReadOnly = (
  table: UnknownStructuredTable,
  rowIndex: number,
  columnIndex: number
) => boolean;

/** Treats every cell of a finalized row as read-only. */
const FINALIZED_ROWS_READ_ONLY: IsCellReadOnly = (table, rowIndex) => table.rows[rowIndex].isFinalized;

export function tableCellKey(rowId: string, columnUuid: string): string {
  return `${rowId}::${columnUuid}`;
}

/** Only `changed` conflicts can be overwritten; a deleted or read-only cell has nowhere to write. */
export function canOverwrite(conflict: TableConflict): boolean {
  return conflict.reason === 'changed';
}

/**
 * The resolution that still applies to `conflict`. "Use theirs" always applies: keeping the live cell
 * loses nothing. "Overwrite" applies only while the cell holds the value the user chose to overwrite.
 */
export function getResolution(
  decisions: TableConflictDecisions,
  conflict: TableConflict
): TableConflictResolution | null {
  const decision = decisions.get(conflict.key);
  if (decision == null || decision.resolution === 'theirs') {
    return decision?.resolution ?? null;
  }
  const seen = decision.conflict;
  const isUnchanged =
    seen.reason === conflict.reason &&
    seen.liveCell != null &&
    conflict.liveCell != null &&
    cellInputEquals(seen.liveCell, conflict.liveCell);
  return isUnchanged ? decision.resolution : null;
}

/** Whether two cells hold the same input. Ignores formatting and the values cached at save. */
export function cellInputEquals(a: Cell, b: Cell): boolean {
  return (a.text ?? '') === (b.text ?? '') && isEqual(a.typedValue ?? null, b.typedValue ?? null);
}

/**
 * The decisions whose edit `draft` still holds. A decision is about the edit the user saw, so it lapses
 * once that cell is edited again.
 */
export function keepDecisionsForDraft(
  decisions: TableConflictDecisions,
  draft: UnknownStructuredTable
): TableConflictDecisions {
  const draftRowById = new Map(draft.rows.map((row) => [row.rowId, row]));
  const draftColumnIndexByUuid = new Map(
    draft.columns.map((column, index) => [column.provenanceUuid, index])
  );
  return new Map(
    [...decisions].filter(([, {conflict}]) => {
      const columnIndex = draftColumnIndexByUuid.get(conflict.columnUuid);
      const draftCell =
        columnIndex == null ? undefined : draftRowById.get(conflict.rowId)?.cells[columnIndex];
      return draftCell != null && cellInputEquals(draftCell, conflict.draftCell);
    })
  );
}

/**
 * Writes the draft's input into the live cell, keeping the live cell's formatting. Clears the cached
 * values so the notebook recomputes them on save.
 */
export function applyDraftCell(target: Cell, draftCell: Cell): void {
  if (draftCell.typedValue != null) {
    target.typedValue = draftCell.typedValue;
  } else {
    delete target.typedValue;
  }
  target.text = draftCell.text ?? '';
  delete target.cachedValue;
  delete target.cachedTypedValue;
}

/**
 * Applies the cells edited in `draft` (relative to `base`, the table the draft started from) to `live`,
 * the table now. Rows match by `rowId` and columns by `provenanceUuid`, so rows or columns added,
 * removed or moved since `base` don't shift the edits. Cells the draft didn't edit keep
 * their live value. An edit conflicts where `isCellReadOnly` says the live cell is read-only, so a
 * finalized row conflicts only where its cells are locked: some tables keep them editable. An edit
 * decided as "Use theirs" is dropped even if it no longer conflicts.
 */
export function mergeDraftIntoLive(
  base: UnknownStructuredTable,
  draft: UnknownStructuredTable,
  live: UnknownStructuredTable,
  decisions: TableConflictDecisions = new Map(),
  isCellReadOnly: IsCellReadOnly = FINALIZED_ROWS_READ_ONLY
): MergeResult {
  const baseRowIndexById = new Map(base.rows.map((row, index) => [row.rowId, index]));
  const liveRowIndexById = new Map(live.rows.map((row, index) => [row.rowId, index]));
  const liveColumnIndexByUuid = new Map(live.columns.map((column, index) => [column.provenanceUuid, index]));

  const conflicts: Array<TableConflict> = [];
  const writes: Array<{rowIndex: number; columnIndex: number; draftCell: Cell}> = [];

  draft.rows.forEach((draftRow) => {
    const baseRowIndex = baseRowIndexById.get(draftRow.rowId);
    if (baseRowIndex == null) {
      return;
    }
    const baseRow = base.rows[baseRowIndex];
    draftRow.cells.forEach((draftCell, columnIndex) => {
      const baseCell = baseRow.cells[columnIndex];
      if (baseCell == null || cellInputEquals(baseCell, draftCell)) {
        return;
      }
      const columnUuid = draft.columns[columnIndex].provenanceUuid;
      const key = tableCellKey(draftRow.rowId, columnUuid);
      if (decisions.get(key)?.resolution === 'theirs') {
        return;
      }
      const liveRowIndex = liveRowIndexById.get(draftRow.rowId);
      const liveColumnIndex = liveColumnIndexByUuid.get(columnUuid);
      const liveRow = liveRowIndex == null ? undefined : live.rows[liveRowIndex];
      const liveCell = liveColumnIndex == null ? undefined : liveRow?.cells[liveColumnIndex];

      let reason: TableConflictReason | null = null;
      if (liveRow == null || liveCell == null) {
        reason = 'deleted';
      } else if (isCellReadOnly(live, liveRowIndex!, liveColumnIndex!)) {
        reason = liveRow.isFinalized ? 'finalized' : 'read-only';
      } else if (cellInputEquals(liveCell, draftCell)) {
        return;
      } else if (!cellInputEquals(liveCell, baseCell)) {
        reason = 'changed';
      }

      if (reason != null) {
        const conflict: TableConflict = {
          key,
          rowId: draftRow.rowId,
          columnUuid,
          reason,
          baseRowIndex,
          draftCell,
          liveCell: liveCell ?? null,
        };
        const resolution = getResolution(decisions, conflict);
        if (resolution == null) {
          conflicts.push(conflict);
          return;
        }
        if (resolution === 'theirs' || !canOverwrite(conflict)) {
          return;
        }
      }
      writes.push({rowIndex: liveRowIndex!, columnIndex: liveColumnIndex!, draftCell});
    });
  });

  const next =
    writes.length === 0
      ? live
      : produce(live, (nextLive) => {
          for (const {rowIndex, columnIndex, draftCell} of writes) {
            applyDraftCell(nextLive.rows[rowIndex].cells[columnIndex], draftCell);
          }
        });
  return {next, conflicts};
}
